import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { PageFetchProgress } from '@/shared/components/page-fetch-progress';

interface PageFetchProgressContextValue {
  begin: () => void;
  end: () => void;
  active: boolean;
}

const PageFetchProgressContext =
  createContext<PageFetchProgressContextValue | null>(null);

export function PageFetchProgressProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [pendingCount, setPendingCount] = useState(0);

  const begin = useCallback(() => {
    setPendingCount((count) => count + 1);
  }, []);

  const end = useCallback(() => {
    setPendingCount((count) => Math.max(0, count - 1));
  }, []);

  const value = useMemo(
    () => ({ begin, end, active: pendingCount > 0 }),
    [begin, end, pendingCount],
  );

  return (
    <PageFetchProgressContext.Provider value={value}>
      {children}
    </PageFetchProgressContext.Provider>
  );
}

/** Renders under the top nav (admin layout). */
export function PageFetchProgressSlot({ className }: { className?: string }) {
  const context = useContext(PageFetchProgressContext);
  return (
    <PageFetchProgress
      active={Boolean(context?.active)}
      className={className}
    />
  );
}

/** Registers a fetch indicator while `active` is true (edit/detail pages). */
export function usePageFetchProgress(active: boolean): void {
  const context = useContext(PageFetchProgressContext);

  useEffect(() => {
    if (!context || !active) {
      return;
    }

    context.begin();
    return () => {
      context.end();
    };
  }, [active, context]);
}
